// src/pages/DashboardPage.js
// Updated: 06/20/2026

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/layout/Layout';
import { useFamily } from '../context/FamilyContext';
import { functions as firebaseFunctions, db } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { collection, query, onSnapshot, orderBy, where, doc, updateDoc, arrayUnion, arrayRemove, deleteDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import CreateEventModal from '../components/dashboard/CreateEventModal';
import ViewRegistrationsModal from '../components/dashboard/ViewRegistrationsModal';
import ManageMemberModal from '../components/dashboard/ManageMemberModal';
import ConfirmationModal from '../components/common/ConfirmationModal';
import ManageAnnouncementModal from '../components/dashboard/ManageAnnouncementModal';
import MergeConfirmationModal from '../components/dashboard/MergeConfirmationModal';
import RemoveMemberOptionsModal from '../components/dashboard/RemoveMemberOptionsModal';
import { FaPlus, FaCalendarAlt, FaUsers, FaPencilAlt, FaUserPlus, FaUserCircle, FaTrash, FaCrown, FaClipboardList, FaUserFriends, FaBullhorn, FaShareAlt, FaUserMinus, FaLink, FaSyncAlt, FaTimes, FaEye } from 'react-icons/fa';
import { format } from 'date-fns';

const deleteEventCallable = httpsCallable(firebaseFunctions, 'deleteEvent');
const mergeUserRecordsCallable = httpsCallable(firebaseFunctions, 'mergeUserRecords');
const removeMemberWithOptionsCallable = httpsCallable(firebaseFunctions, 'removeMemberAndPreserveRecord');

const DashboardPage = () => {
  const { familyData, isHost } = useFamily();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState({ events: true, members: true, announcements: true });
  const [modal, setModal] = useState({ type: null, data: null });

  // --- NEW Event Tab State ---
  const [eventTab, setEventTab] = useState('upcoming'); // 'upcoming', 'ongoing', 'past'

  // --- Merge State ---
  const [isMerging, setIsMerging] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);

  // --- Fetch Callbacks ---
  const fetchEvents = useCallback(() => { if (!familyData?.id) return; const q = query(collection(db, `families/${familyData.id}/events`), orderBy('startDate', 'desc')); const unsub = onSnapshot(q, (snap) => { setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(s => ({...s, events: false})); }); return unsub; }, [familyData?.id]);
  const fetchMembers = useCallback(() => { if (!familyData?.id) return; const q = query(collection(db, 'users'), where('familyId', '==', familyData.id)); const unsub = onSnapshot(q, (snap) => { setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(s => ({...s, members: false})); }); return unsub; }, [familyData?.id]);
  const fetchAnnouncements = useCallback(() => { if (!familyData?.id) return; const q = query(collection(db, `families/${familyData.id}/announcements`), orderBy('createdAt', 'desc')); const unsub = onSnapshot(q, (snap) => { setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(s => ({...s, announcements: false})); }); return unsub; }, [familyData?.id]);

  // --- useEffects ---
  useEffect(() => { const unsub = fetchEvents(); return () => { if (unsub) unsub(); }; }, [fetchEvents]);
  useEffect(() => { const unsub = fetchMembers(); return () => { if (unsub) unsub(); }; }, [fetchMembers]);
  useEffect(() => { const unsub = fetchAnnouncements(); return () => { if (unsub) unsub(); }; }, [fetchAnnouncements]);

  // --- Handlers ---
  const handleDeleteEvent = async (event) => { try { await deleteEventCallable({ familyId: familyData.id, eventId: event.id }); toast.success('Event deleted!'); } catch (error) { toast.error(error.message); } setModal({ type: null, data: null }); };
  const handlePromoteMember = async (member) => { const familyDocRef = doc(db, 'families', familyData.id); try { await updateDoc(familyDocRef, { hosts: arrayUnion(member.id) }); toast.success(`${member.firstName} is now a host!`); } catch (error) { toast.error("Failed to promote member."); } setModal({ type: null, data: null }); };
  const handleDemoteMember = async (member) => { const familyDocRef = doc(db, 'families', familyData.id); try { await updateDoc(familyDocRef, { hosts: arrayRemove(member.id) }); toast.success(`${member.firstName} is no longer a host.`); } catch (error) { toast.error("Failed to demote member."); } setModal({ type: null, data: null }); };
  const handleDeleteAnnouncement = async (announcementId) => { try { await deleteDoc(doc(db, `families/${familyData.id}/announcements/${announcementId}`)); toast.success('Announcement deleted.'); } catch (error) { toast.error('Failed to delete announcement.'); } setModal({ type: null, data: null }); };
  const copyInviteCode = () => { navigator.clipboard.writeText(familyData.inviteCode); toast.success("Invite code copied to clipboard!"); };
  const copyInviteLink = () => {
    const baseUrl = window.location.origin;
    const inviteLink = `${baseUrl}/login?inviteCode=${familyData.inviteCode}`;
    navigator.clipboard.writeText(inviteLink);
    toast.success("Sharable invite link copied to clipboard!");
  };

  const executeRemoveMember = async (keepRecord) => {
    if (!modal.data) return;
    const memberToRemove = modal.data;
    setModal(prev => ({ ...prev, loading: true }));

    try {
        const result = await removeMemberWithOptionsCallable({
            familyId: familyData.id,
            userIdToRemove: memberToRemove.id,
            keepTreeRecord: keepRecord
        });
        toast.success(result.data.message || 'Member removed successfully!');
        setModal({ type: null, data: null });
    } catch (error) {
        console.error("Remove member failed:", error);
        toast.error(error.message || 'Failed to remove member.');
        setModal({ type: null, data: null, loading: false });
    }
  };

  // --- Merge Handlers ---
  const startMerge = () => {
    setIsMerging(true);
    setMergeSource(null);
    setMergeTarget(null);
    toast('Merge Mode: Select the MANUAL record first (no email), then the AUTHENTICATED record (with email).');
  };

  const cancelMerge = () => {
    setIsMerging(false);
    setMergeSource(null);
    setMergeTarget(null);
  };

  const handleMemberClickForMerge = (member) => {
    if (!isMerging) return;
    if (!mergeSource) {
      if (member.email) { toast.error("Please select the manually added record (without email) first."); return; }
      setMergeSource(member);
      toast.success(`Selected ${member.firstName} (Manual) as source. Now select the authenticated record.`);
    } else {
      if (!member.email) { toast.error("Please select the authenticated record (with email) as the target."); return; }
      if (member.id === mergeSource.id) { toast.error("Cannot merge a user with themselves."); return; }
      setMergeTarget(member);
      setModal({ type: 'confirmMerge', data: { manual: mergeSource, auth: member } });
    }
  };

  const executeMerge = async () => {
    if (!modal.data?.manual || !modal.data?.auth) return;
    const manual = modal.data.manual;
    const auth = modal.data.auth;
    setModal(prev => ({ ...prev, loading: true }));

    try {
        const result = await mergeUserRecordsCallable({
            familyId: familyData.id,
            manualUserId: manual.id,
            authenticatedUserId: auth.id
        });
        toast.success(result.data.message || 'Merge successful!');
        setModal({ type: null, data: null });
        cancelMerge();
    } catch (error) {
        console.error("Merge failed:", error);
        toast.error(error.message || 'Merge failed. Please try again.');
        setModal({ type: null, data: null, loading: false });
        cancelMerge();
    }
  };

  // --- Event Sorting & Filtering ---
  const sortedAndFilteredEvents = useMemo(() => {
    const now = new Date();
    const filtered = events.filter(event => {
        const start = event.startDate.toDate();
        const end = event.endDate.toDate();

        if (eventTab === 'upcoming') return start > now;
        if (eventTab === 'ongoing') return start <= now && end >= now;
        if (eventTab === 'past') return end < now;
        return true;
    });

    // If viewing upcoming, sort ascending so the nearest event is at the top
    if (eventTab === 'upcoming') {
        return filtered.sort((a, b) => a.startDate.toDate() - b.startDate.toDate());
    }
    // Past and Ongoing remain descending (most recent first)
    return filtered.sort((a, b) => b.startDate.toDate() - a.startDate.toDate());
  }, [events, eventTab]);

  // --- Alphabetical Sorting for Members ---
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
        const lastNameA = a.lastName || '';
        const lastNameB = b.lastName || '';
        const firstNameA = a.firstName || '';
        const firstNameB = b.firstName || '';
        if (lastNameA.toLowerCase() < lastNameB.toLowerCase()) return -1;
        if (lastNameA.toLowerCase() > lastNameB.toLowerCase()) return 1;
        if (firstNameA.toLowerCase() < firstNameB.toLowerCase()) return -1;
        if (firstNameA.toLowerCase() > firstNameB.toLowerCase()) return 1;
        return 0;
    });
  }, [members]);


  if (!isHost) { return ( <Layout><div className="text-center py-10"><h1 className="text-2xl font-bold">Access Denied</h1></div></Layout> ); }

  return (
    <Layout>
      {/* Modals */}
      {modal.type === 'removeMemberWithOptions' && (
        <RemoveMemberOptionsModal
            member={modal.data}
            onConfirm={executeRemoveMember}
            onCancel={() => setModal({ type: null, data: null })}
            zIndexClass="z-[60]"
        />
      )}
      {modal.type === 'confirmMerge' && (
        <MergeConfirmationModal
          manualUser={modal.data?.manual}
          authUser={modal.data?.auth}
          onConfirm={executeMerge}
          onCancel={() => { setModal({ type: null, data: null }); }}
          zIndexClass="z-[60]"
        />
      )}
      {modal.type === 'createEvent' && <CreateEventModal familyId={familyData.id} allMembers={members} onClose={() => setModal({ type: null, data: null })} onEventCreated={() => setModal({ type: null, data: null })} />}
      {modal.type === 'editEvent' && <CreateEventModal familyId={familyData.id} allMembers={members} existingEvent={modal.data} onClose={() => setModal({ type: null, data: null })} onEventCreated={() => setModal({ type: null, data: null })} />}
      {modal.type === 'viewReg' && <ViewRegistrationsModal familyId={familyData.id} event={modal.data} onClose={() => setModal({ type: null, data: null })} />}
      {modal.type === 'addMember' && <ManageMemberModal familyId={familyData.id} allMembers={members} onClose={() => setModal({ type: null, data: null })} onMemberAdded={() => setModal({ type: null, data: null })} />}
      {modal.type === 'editMember' && <ManageMemberModal familyId={familyData.id} existingMember={modal.data} allMembers={members} onClose={() => setModal({ type: null, data: null })} onMemberAdded={() => setModal({ type: null, data: null })} />}
      {modal.type === 'addAnnouncement' && <ManageAnnouncementModal familyId={familyData.id} onClose={() => setModal({ type: null, data: null })} onAnnouncementAdded={() => setModal({ type: null, data: null })} />}
      {modal.type === 'editAnnouncement' && <ManageAnnouncementModal familyId={familyData.id} existingAnnouncement={modal.data} onClose={() => setModal({ type: null, data: null })} onAnnouncementAdded={() => setModal({ type: null, data: null })} />}
      {modal.type === 'confirmDeleteEvent' && <ConfirmationModal title="Delete Event?" message={`Are you sure you want to delete "${modal.data.eventName}"?`} onConfirm={() => handleDeleteEvent(modal.data)} onCancel={() => setModal({ type: null, data: null })} />}
      {modal.type === 'confirmPromoteMember' && <ConfirmationModal title="Promote to Host?" message={`Are you sure you want to make ${modal.data.firstName} a host?`} onConfirm={() => handlePromoteMember(modal.data)} onCancel={() => setModal({ type: null, data: null })} confirmText="Promote" confirmColor="blue" />}
      {modal.type === 'confirmDemoteMember' && <ConfirmationModal title="Demote Host?" message={`Are you sure you want to remove host privileges from ${modal.data.firstName}?`} onConfirm={() => handleDemoteMember(modal.data)} onCancel={() => setModal({ type: null, data: null })} confirmText="Demote" confirmColor="red" />}
      {modal.type === 'confirmDeleteAnnouncement' && <ConfirmationModal title="Delete Announcement?" message="Are you sure you want to delete this announcement?" onConfirm={() => handleDeleteAnnouncement(modal.data.id)} onCancel={() => setModal({ type: null, data: null })} />}

      {/* Dashboard Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-neutral-gray">Host Dashboard</h1>
         <button onClick={() => setModal({ type: 'createEvent' })} className="flex items-center gap-2 px-4 py-2 bg-cta-orange text-white rounded-md hover:bg-orange-700 font-medium shadow w-full sm:w-auto justify-center"><FaPlus /> Create Event</button>
      </div>

       {/* Merge Mode Indicator */}
        {isMerging && (
            <div className="mb-4 p-3 bg-blue-100 border border-blue-300 rounded-md text-blue-800 text-sm">
                <p><span className="font-semibold">Merge Mode Active:</span> Select the <span className="font-bold">manual record (no email)</span> first, then the <span className="font-bold">authenticated record (with email)</span> to merge.</p>
                {mergeSource && <p className="mt-1">Selected source: <span className="font-semibold">{mergeSource.firstName} {mergeSource.lastName} (Manual)</span></p>}
            </div>
        )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          
          {/* Manage Events Section */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h2 className="text-xl font-semibold">Manage Events</h2>
              
              {/* Event Filter Tabs */}
              <div className="flex bg-gray-100 p-1 rounded-md w-full sm:w-auto">
                <button 
                    onClick={() => setEventTab('upcoming')} 
                    className={`flex-1 sm:flex-none px-4 py-1.5 text-sm rounded-md transition-all duration-200 ${eventTab === 'upcoming' ? 'bg-white shadow text-primary font-semibold' : 'text-gray-500 hover:bg-gray-200'}`}
                >
                    Upcoming
                </button>
                <button 
                    onClick={() => setEventTab('ongoing')} 
                    className={`flex-1 sm:flex-none px-4 py-1.5 text-sm rounded-md transition-all duration-200 ${eventTab === 'ongoing' ? 'bg-white shadow text-primary font-semibold' : 'text-gray-500 hover:bg-gray-200'}`}
                >
                    Ongoing
                </button>
                <button 
                    onClick={() => setEventTab('past')} 
                    className={`flex-1 sm:flex-none px-4 py-1.5 text-sm rounded-md transition-all duration-200 ${eventTab === 'past' ? 'bg-white shadow text-primary font-semibold' : 'text-gray-500 hover:bg-gray-200'}`}
                >
                    Past
                </button>
              </div>
            </div>

            {loading.events ? ( <p className="text-gray-500 animate-pulse">Loading events...</p> ) : sortedAndFilteredEvents.length > 0 ? (
              <ul className="space-y-4">
                {sortedAndFilteredEvents.map(event => (
                  <li key={event.id} className={`p-4 border rounded-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-gray-50 group transition-colors ${eventTab === 'past' ? 'bg-gray-50 opacity-75 grayscale-[0.5]' : ''}`}>
                    <div className="cursor-pointer" onClick={() => navigate(`/event/${event.id}`)}>
                      <h3 className="font-semibold text-lg text-primary hover:underline">{event.eventName}</h3>
                      <p className="text-gray-500 text-sm flex items-center gap-2 mt-1">
                        <FaCalendarAlt />
                        {format(event.startDate.toDate(), 'MMM d, yyyy')} - {format(event.endDate.toDate(), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                      <button onClick={() => navigate(`/event/${event.id}`)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 bg-primary text-white text-sm rounded-md hover:bg-blue-700"><FaEye size={12} /> View Details</button>
                      <button onClick={() => setModal({ type: 'editEvent', data: event })} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-sm text-gray-700 rounded-md hover:bg-gray-50"><FaPencilAlt size={12} /> Edit</button>
                      <button onClick={() => setModal({ type: 'viewReg', data: event })} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-sm text-gray-700 rounded-md hover:bg-gray-50"><FaUsers /> Registrations</button>
                      <button onClick={() => setModal({ type: 'confirmDeleteEvent', data: event })} className="text-gray-400 hover:text-red-600 p-2 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity" title="Delete Event"><FaTrash size={14}/></button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : ( 
              <div className="text-center py-12">
                <FaClipboardList className="mx-auto text-5xl text-gray-300"/>
                <h3 className="mt-4 text-lg font-medium text-gray-800">No {eventTab.charAt(0).toUpperCase() + eventTab.slice(1)} Events</h3>
                <p className="text-gray-500 mt-2">
                    {eventTab === 'upcoming' ? 'Click "Create Event" to schedule a new one.' : `You have no ${eventTab} events to display.`}
                </p>
              </div> 
            )}
          </div>
          
          {/* Announcements Section */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-semibold">Announcements</h2><button onClick={() => setModal({ type: 'addAnnouncement'})} className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white text-sm rounded-md hover:bg-blue-700 font-medium"><FaBullhorn/> Post New</button></div>
            {loading.announcements ? (<p>Loading...</p>) : announcements.length > 0 ? (
              <ul className="space-y-4 max-h-96 overflow-y-auto">
                {announcements.map(item => (
                  <li key={item.id} className="p-3 border rounded-md group"> 
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">{item.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{item.message}</p>
                      </div>
                      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setModal({ type: 'editAnnouncement', data: item })} className="p-1 text-gray-400 hover:text-primary" title="Edit Announcement"><FaPencilAlt size={12}/></button>
                        <button onClick={() => setModal({ type: 'confirmDeleteAnnouncement', data: item })} className="p-1 text-gray-400 hover:text-red-500" title="Delete Announcement"><FaTrash size={12}/></button>
                      </div>
                    </div>
                    {/* FIXED: Safe fallback for createdAt */}
                    <p className="text-xs text-gray-400 mt-2">
                      By {item.author} on {item.createdAt ? format(item.createdAt.toDate(), 'MMM d, yyyy') : 'Just now'}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (<div className="text-center py-12"><FaBullhorn className="mx-auto text-5xl text-gray-300"/><h3 className="mt-4 text-lg font-medium">No Announcements</h3><p className="text-gray-500 mt-2">Click "Post New" to share an update with the family.</p></div>)}
          </div>
        </div>
        <div className="space-y-8">
            {/* Invite Code Section */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-2">Invite Family Members</h2>
                <p className="text-sm text-gray-500 mb-4">Share the code or the link below with relatives so they can join your family group.</p>
                <div className="p-3 bg-gray-100 rounded-md flex items-center justify-between">
                    <span className="text-2xl font-mono font-bold text-neutral-gray tracking-widest">{familyData?.inviteCode}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={copyInviteLink} className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 font-medium" title="Copy Invite Link">
                          <FaLink/>
                      </button>
                      <button onClick={copyInviteCode} className="px-3 py-1.5 bg-primary text-white text-sm rounded-md hover:bg-blue-700 font-medium" title="Copy Code Only">
                          <FaShareAlt/>
                      </button>
                    </div>
                </div>
            </div>
            {/* Family Members Section */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
                    <h2 className="text-xl font-semibold">Family Members</h2>
                    <div className="flex items-center gap-2">
                         {!isMerging ? (
                            <button onClick={startMerge} title="Merge Duplicates" className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 font-medium">
                                <FaSyncAlt /> <span className="hidden sm:inline">Merge</span>
                            </button>
                         ) : (
                              <button onClick={cancelMerge} title="Cancel Merge" className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 font-medium">
                                 <FaTimes /> <span className="hidden sm:inline">Cancel</span>
                             </button>
                         )}
                        <button onClick={() => setModal({ type: 'addMember'})} className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white text-sm rounded-md hover:bg-blue-700 font-medium"><FaUserPlus/> Add</button>
                    </div>
                </div>
                {loading.members ? (<p>Loading...</p>) : sortedMembers.length > 0 ? (
                    <ul className="space-y-3 max-h-96 overflow-y-auto">{sortedMembers.map(member => (
                        <li
                           key={member.id}
                           className={`flex items-center justify-between gap-3 group rounded-md p-2 transition-colors ${
                               isMerging ? 'cursor-pointer hover:bg-gray-100' : ''
                           } ${
                               isMerging && mergeSource?.id === member.id ? 'bg-red-100 ring-2 ring-red-300' : ''
                           } ${
                               isMerging && mergeTarget?.id === member.id ? 'bg-green-100 ring-2 ring-green-300' : ''
                           }`}
                           onClick={() => handleMemberClickForMerge(member)}
                        >
                            <div className="flex items-center gap-3 overflow-hidden"><div className="flex-shrink-0">{member.profilePictureUrl ? (<img src={member.profilePictureUrl} alt="" className="h-8 w-8 rounded-full object-cover"/>) : (<FaUserCircle className="h-8 w-8 text-gray-300"/>)}</div><div className="truncate"><p className="text-sm font-medium text-gray-900 truncate">{member.firstName} {member.lastName} {familyData?.hosts.includes(member.id) && <FaCrown className="inline mb-1 ml-1 text-yellow-500" title="Host"/>}</p><p className="text-xs text-gray-500 truncate">{member.email || (member.isDeceased ? 'Deceased' : 'No account')}</p></div></div>
                            {!isMerging && (
                                <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                    <button onClick={(e) => { e.stopPropagation(); setModal({ type: 'editMember', data: member }); }} className="p-1 text-gray-400 hover:text-primary" title="Edit Member"><FaPencilAlt size={12}/></button>
                                    
                                    {/* HOST DEMOTION */}
                                    {familyData?.hosts.includes(member.id) && familyData?.hosts[0] !== member.id && (<button onClick={(e) => { e.stopPropagation(); setModal({ type: 'confirmDemoteMember', data: member }); }} className="p-1 text-gray-400 hover:text-red-500" title="Demote Host"><FaUserMinus size={12}/></button>)}
                                    
                                    {/* HOST PROMOTION - ONLY VISIBLE IF MEMBER HAS AN EMAIL */}
                                    {!familyData?.hosts.includes(member.id) && member.email && <button onClick={(e) => { e.stopPropagation(); setModal({ type: 'confirmPromoteMember', data: member }); }} className="p-1 text-gray-400 hover:text-yellow-500" title="Promote to Host"><FaCrown size={12}/></button>}
                                    
                                    {familyData?.hosts[0] !== member.id && <button
                                        onClick={(e) => { e.stopPropagation(); setModal({ type: 'removeMemberWithOptions', data: member }); }}
                                        className="p-1 text-gray-400 hover:text-red-600" title="Remove Member"><FaTrash size={12}/></button>
                                    }
                                </div>
                            )}
                        </li>
                    ))}</ul>
                ) : ( <div className="text-center py-12"><FaUserFriends className="mx-auto text-5xl text-gray-300"/><h3 className="mt-4 text-lg font-medium text-gray-800">No Members Found</h3><p className="text-gray-500 mt-2">Click "Add" to manually add family members who can't register themselves.</p></div> )}
            </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;